// src/pages/AdminAuth/components/authStore.js
export const AUTH_FLOW_KEY = "ADMIN_AUTH_FLOW";
export const AUTH_TOKEN_KEY = "ADMIN_AUTH_TOKEN";
export const AUTH_USER_KEY = "ADMIN_AUTH_USER";
export const AUTH_SESSION_KEY = "ADMIN_AUTH_SESSION";
export const AUTH_STATE_EVENT = "wholesum-auth-state-changed";

function safeJsonParse(value, fallback) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function emitAuthStateChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_STATE_EVENT));
  }
}

export function getAuthFlow() {
  return safeJsonParse(localStorage.getItem(AUTH_FLOW_KEY) || "{}", {});
}

export function setAuthFlow(patch) {
  const cur = getAuthFlow();
  const next = { ...cur, ...patch };
  localStorage.setItem(AUTH_FLOW_KEY, JSON.stringify(next));
  emitAuthStateChange();
  return next;
}

export function clearAuthFlow() {
  localStorage.removeItem(AUTH_FLOW_KEY);
  emitAuthStateChange();
}

export function getAuthToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY) || "";
}

export function getAuthUser() {
  const raw = localStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;
  return safeJsonParse(raw, null);
}

export function getAuthSession() {
  const raw = localStorage.getItem(AUTH_SESSION_KEY);
  if (!raw) return {};
  return safeJsonParse(raw, {});
}

export function setAuthSession({
  accessToken,
  admin,
  dashboardAccess,
  warehouseAccess,
  currentDashboard,
  selectedWarehouseId,
}) {
  if (accessToken) localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
  if (admin) localStorage.setItem(AUTH_USER_KEY, JSON.stringify(admin));

  const current = getAuthSession();
  const next = {
    ...current,
    ...(dashboardAccess ? { dashboardAccess } : {}),
    ...(warehouseAccess ? { warehouseAccess } : {}),
    ...(currentDashboard ? { currentDashboard } : {}),
    ...(selectedWarehouseId !== undefined ? { selectedWarehouseId } : {}),
  };

  localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(next));
  emitAuthStateChange();
  return next;
}

export function clearAuthSession() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(AUTH_SESSION_KEY);
  emitAuthStateChange();
}

export function clearAllAuthState() {
  clearAuthFlow();
  clearAuthSession();
}

export function updateAuthUser(patch) {
  const current = getAuthUser() || {};
  const next = { ...current, ...patch };
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(next));
  emitAuthStateChange();
  return next;
}

export function updateAuthSession(patch) {
  const current = getAuthSession() || {};
  const next = { ...current, ...patch };
  localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(next));
  emitAuthStateChange();
  return next;
}

export function getDashboardAccess() {
  return getAuthSession()?.dashboardAccess || {};
}

export function getWarehouseAccess() {
  return getAuthSession()?.warehouseAccess || {};
}

export function getCurrentDashboard() {
  return getAuthSession()?.currentDashboard || "admin";
}

export function getSelectedWarehouseId() {
  return getAuthSession()?.selectedWarehouseId || null;
}

export function canOpenAdminDashboard() {
  return !!getDashboardAccess()?.canOpenAdminDashboard;
}

export function canOpenWarehouseDashboard() {
  return !!getDashboardAccess()?.canOpenWarehouseDashboard;
}

export function isAuthSessionValid() {
  const token = getAuthToken();
  const user = getAuthUser();
  const session = getAuthSession();
  const dashboardAccess = session?.dashboardAccess || {};

  if (!token) return false;
  if (!user || typeof user !== "object") return false;
  if (!session || typeof session !== "object") return false;

  const canOpenAdmin = !!dashboardAccess?.canOpenAdminDashboard;
  const canOpenWarehouse = !!dashboardAccess?.canOpenWarehouseDashboard;

  if (!canOpenAdmin && !canOpenWarehouse) return false;

  return true;
}

function getEffectivePermissions() {
  const user = getAuthUser() || {};
  const session = getAuthSession() || {};

  const raw = [
    ...(Array.isArray(user?.permissions) ? user.permissions : []),
    ...(Array.isArray(session?.permissions) ? session.permissions : []),
  ];

  return Array.from(new Set(raw.filter(Boolean).map((p) => String(p).trim())));
}

function buildPermissionSet() {
  const raw = getEffectivePermissions();
  const granted = new Set(raw);

  const addMany = (items = []) => {
    items.forEach((item) => {
      const key = String(item || "").trim();
      if (key) granted.add(key);
    });
  };

  raw.forEach((perm) => {
    const key = String(perm || "").trim();

    // Catalog read/write aliases. Manage implies read, but read never implies manage.
    if (
      [
        "products.view",
        "products.read",
        "admin.products.read",
        "categories.view",
        "categories.read",
        "brands.view",
        "brands.read",
        "units.view",
        "units.read",
      ].includes(key)
    ) {
      addMany(["admin.catalog.read"]);
    }

    if (
      [
        "products.create",
        "products.update",
        "products.delete",
        "products.approve",
        "categories.create",
        "categories.update",
        "categories.delete",
        "brands.create",
        "brands.update",
        "brands.delete",
        "units.create",
        "units.update",
        "units.delete",
      ].includes(key)
    ) {
      addMany(["admin.catalog.manage", "admin.catalog.read"]);
    }

    if (key === "admin.catalog.manage") {
      addMany(["admin.catalog.read"]);
    }

    // Read/write module permissions. Manage implies read, but read does not imply manage.
    const manageToRead = {
      "admin.settings.update": "admin.settings.read",
      "admin.posters.manage": "admin.posters.read",
      "admin.short_videos.manage": "admin.short_videos.read",
      "admin.broadcasts.manage": "admin.broadcasts.read",
      "admin.reports.export": "admin.reports.read",
      "admin.app_layout.manage": "admin.app_layout.read",
      "admin.driver_wallet.manage": "admin.driver_wallet.read",
    };

    if (manageToRead[key]) {
      addMany([manageToRead[key]]);
    }

    // Warehouse/inventory aliases kept for existing pages.
    if (["wh.inbound.accept", "admin.catalog.manage"].includes(key)) {
      addMany(["wh.inventory.read", "warehouse.inventory.read", "admin.inventory.read"]);
    }

 if (["wh.orders.read", "wh.orders.export", "warehouse.orders.read"].includes(key)) {
  addMany(["wh.orders.read", "warehouse.orders.read"]);
}

if (["wh.orders.create", "warehouse.orders.create"].includes(key)) {
  addMany(["wh.orders.create", "warehouse.orders.create"]);
}

if (["wh.inventory.read", "warehouse.inventory.read", "warehouse.stock.read"].includes(key)) {
  addMany(["wh.inventory.read", "warehouse.inventory.read", "warehouse.stock.read"]);
}

if (["wh.inbound.read", "warehouse.inbound.read"].includes(key)) {
  addMany(["wh.inbound.read", "warehouse.inbound.read"]);
}

if (["wh.inbound.accept", "warehouse.inbound.accept"].includes(key)) {
  addMany(["wh.inbound.accept", "warehouse.inbound.accept", "wh.inbound.read"]);
}

if (["wh.products.read", "warehouse.products.read"].includes(key)) {
  addMany(["wh.products.read", "warehouse.products.read"]);
}

if (["wh.employees.read", "warehouse.employees.read", "warehouse_employee.read"].includes(key)) {
  addMany(["wh.employees.read", "warehouse.employees.read", "warehouse_employee.read"]);
}

if (["wh.employees.create", "warehouse.employees.create", "warehouse_employee.create"].includes(key)) {
  addMany(["wh.employees.create", "warehouse.employees.create", "warehouse_employee.create"]);
}

if (["wh.employees.update", "warehouse.employees.update", "warehouse_employee.update"].includes(key)) {
  addMany(["wh.employees.update", "warehouse.employees.update", "warehouse_employee.update"]);
}

if (["wh.employees.delete", "warehouse.employees.delete", "warehouse_employee.delete"].includes(key)) {
  addMany(["wh.employees.delete", "warehouse.employees.delete", "warehouse_employee.delete"]);
}

if (["wh.employees.approve", "warehouse.employees.approve", "warehouse_employee.approve"].includes(key)) {
  addMany(["wh.employees.approve", "warehouse.employees.approve", "warehouse_employee.approve"]);
}
  });

  return granted;
}

export function isSuperAdminAccount() {
  const user = getAuthUser() || {};
  const session = getAuthSession() || {};

  const role = String(
    user?.role ||
      user?.roleName ||
      user?.adminRole ||
      session?.role ||
      session?.roleName ||
      ""
  )
    .trim()
    .toLowerCase();

  const userType = String(user?.userType || user?.user_type || "")
    .trim()
    .toLowerCase();

  return (
    user?.isSuperAdmin === true ||
    user?.isAdmin === true ||
    role === "super_admin" ||
    role === "superadmin" ||
    userType === "super_admin"
  );
}

export function hasPermission(keys) {
  if (isSuperAdminAccount()) return true;

  const granted = buildPermissionSet();

  if (!Array.isArray(keys)) {
    const key = String(keys || "").trim();
    return !!key && granted.has(key);
  }

  return keys.some((item) => {
    const key = String(item || "").trim();
    return !!key && granted.has(key);
  });
}